
import React, { useState } from 'react';

// --- Mock placeholders / helpers (در پروژه واقعی این‌ها را با نسخه‌های اصلی عوض کن) ---
const Badge = ({ className = '', children }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
    {children}
  </span>
);

const Button = ({ className = '', onClick, children }) => (
    <button
        type="button"
        className={`py-1.5 px-3 rounded-lg text-sm transition-colors duration-200 ${className}`}
        onClick={onClick}
    >
        {children}
    </button>
);

const Clipboard = (props) => (
    <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2m-9 0V3h4v2m-4 0h4"
        />
    </svg>
);

const UserPlus = (props) => (
    <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zm-2 5h-4a2 2 0 00-2 2v2a2 2 0 002 2h4a2 2 0 002-2v-2a2 2 0 00-2-2z"
        />
    </svg>
);

const Info = (props) => (
    <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
    </svg>
);

// Mock helper functions (در پروژه واقعی با نسخه‌ی اصلی عوض کن)
const fa = { format: (n) => n.toLocaleString('fa-IR') };
const faDate = (date) => (date ? new Date(date).toLocaleDateString('fa-IR') : '—');
const fullName = (buyer) => `${buyer?.first_name || ''} ${buyer?.last_name || ''}`.trim() || 'بدون نام';

const unitCommission = (license) => {
    const price = Number(license.authorized_price ?? 0);
    const pct = Number(license.agent_commission_pct ?? license.product?.agent_commission_pct ?? 0);
    return Math.round((price * pct) / 100);
};

const humanAssign = (license) => (license.buyer_id ? 'اختصاص یافته' : 'بدون خریدار');
const humanActive = (license) => (license.status === 'active' ? 'فعال' : 'غیرفعال');

const toast = { success: (msg) => console.log(`Toast: ${msg}`) };

// در پروژه واقعی navigator از مرورگر در دسترس است
const safeNavigator =
    typeof navigator !== 'undefined' ? navigator : { clipboard: { writeText: () => Promise.resolve() } };

// --------- Accordion helpers ---------
const SECTION_KEYS = {
    BUYER: 'buyer',
    FINANCIAL: 'financial',
    TIMELINE: 'timeline',
    META: 'meta',
};

/**
 * کامپوننت کارت برای یک لایسنس
 * props:
 *  - license        (ضروری)
 *  - onAssignBuyer  (اختیاری)  => تابعی که بیرون برای باز کردن مودال می‌نویسی
 *  - onOpenDetails  (اختیاری)  => برای رفتن به صفحه / مودال جزئیات
 */
export default function LicenseBoardingPassCardRTL({ license, onAssignBuyer, onOpenDetails }) {
    const [openKey, setOpenKey] = useState(null); // کدوم تب آکاردئون باز است

    if (!license) return null;

    const id = String(license.id);
    const productTitle =
        license.product_title || license.product?.title || `محصول ${license.product_id}`;
    const buyerName = license.buyer ? fullName(license.buyer) : 'خریدار ثبت نشده';
    const buyerPhone = license.buyer?.phone_number || '—';
    const perUnitCommission = unitCommission(license);
    const price = Number(license.authorized_price ?? 0);
    const canAssign = !license.buyer_id;

    const toggleSection = (key) => {
        setOpenKey((current) => (current === key ? null : key));
    };

    const handleOpenDetails = () => {
        if (onOpenDetails) onOpenDetails(license);
        else console.log('Opening details for license:', license.license_key);
    };

    const handleAssignBuyer = () => {
        if (onAssignBuyer) onAssignBuyer(license);
    };

    return (
        <div
            dir="rtl"
            className="relative overflow-hidden rounded-2xl border border-emerald-600/60 bg-gradient-to-br from-emerald-50/60 to-white shadow-md hover:shadow-lg transition-all duration-300"
        >
            {/* بالا: نوار کارت پرواز مستطیلی با گرید اطلاعات اصلی */}
            <div className="flex flex-col md:flex-row md:items-stretch">
                {/* ستون اصلی اطلاعات مهم */}
                <div className="flex-1 p-4 md:p-5 flex flex-col justify-between border-b md:border-b-0 md:border-l border-emerald-50">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full text-right">
                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold">محصول</div>
                            <div className="text-lg font-bold text-gray-900 truncate">{productTitle}</div>
                            <div className="mt-1 text-[11px] font-mono text-emerald-700" dir="ltr">
                                {license.license_key}
                            </div>
                        </div>

                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold">خریدار</div>
                            <div className="font-medium text-sky-900 text-[13px] text-gray-900">{buyerName}</div>
                            <div dir="ltr" className="text-xs text-gray-500 font-mono mt-0.5">
                                {buyerPhone}
                            </div>
                        </div>

                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold">وضعیت</div>
                            <div className="flex flex-col items-start gap-1 mt-1">
                                <Badge
                                    className={
                                        license.buyer_id
                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                            : 'bg-red-50 text-red-700 border border-red-200'
                                    }
                                >
                                    {humanAssign(license)}
                                </Badge>
                                <Badge
                                    className={
                                        humanActive(license) === 'فعال'
                                            ? 'bg-emerald-500 text-white'
                                            : 'bg-gray-300 text-gray-700'
                                    }
                                >
                                    {humanActive(license)}
                                </Badge>
                            </div>
                        </div>

                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold">قیمت مجاز</div>
                            <div className="font-semibold text-gray-900">
                                {fa.format(price / 10)} <span className="text-xs">تومان</span>
                            </div>
                        </div>

                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold">کمیسیون شما</div>
                            <div className="font-bold text-emerald-700">
                                {fa.format(perUnitCommission / 10)} <span className="text-xs">تومان</span>
                            </div>
                        </div>

                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold">شناسه سفارش</div>
                            <div className="font-mono text-sm text-gray-900">{license.reference_code}</div>
                        </div>

                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold">تاریخ صدور</div>
                            <div className="text-gray-800">{faDate(license.issued_at)}</div>
                        </div>

                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold">تاریخ انقضا</div>
                            <div className="text-gray-800">{faDate(license.expires_at)}</div>
                        </div>

                        <div className="flex items-center gap-2 justify-start col-span-2 md:col-span-1 mt-2">
                        {/*    <Button*/}
                        {/*        className="border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 flex items-center gap-2"*/}
                        {/*        onClick={() =>*/}
                        {/*            safeNavigator.clipboard*/}
                        {/*                .writeText(license.license_key)*/}
                        {/*                .then(() => toast.success('کپی شد'))*/}
                        {/*        }*/}
                        {/*    >*/}
                        {/*        <Clipboard className="h-4 w-4" />*/}
                        {/*        کپی کلید*/}
                        {/*    </Button>*/}
                        {/*    <Button*/}
                        {/*        className="border border-emerald-400 text-emerald-700 hover:bg-emerald-50 flex items-center gap-2"*/}
                        {/*        onClick={handleOpenDetails}*/}
                        {/*    >*/}
                        {/*        <Info className="h-4 w-4" />*/}
                        {/*        جزئیات کامل*/}
                        {/*    </Button>*/}
                        </div>
                    </div>
                </div>

                {/* ستون کناری: اکشن‌های نماینده / کارت پروازی */}
                <div className="w-full md:w-40 p-4 flex flex-col justify-between bg-gradient-to-b from-emerald-50/80 to-white">
                    <div className="flex flex-col gap-2">
                        <Button
                            className="flex items-center justify-center gap-2 border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                            onClick={() =>
                                safeNavigator.clipboard
                                    .writeText(license.license_key)
                                    .then(() => toast.success('کلید لایسنس کپی شد'))
                            }
                        >
                            <Clipboard className="h-4 w-4" />
                            کپی کلید
                        </Button>

                        {canAssign && (
                            <Button
                                className="flex items-center justify-center gap-2 bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 shadow-sm"
                                onClick={handleAssignBuyer}
                            >
                                <UserPlus className="h-4 w-4" />
                                اختصاص دادن
                            </Button>
                        )}

                        <Button
                            className="flex items-center justify-center gap-2 border border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                            onClick={handleOpenDetails}
                        >
                            <Info className="h-4 w-4" />
                            جزئیات کامل
                        </Button>
                    </div>

                    {/* کد شبه بارکد / دندانه‌ای تزئینی */}
                    <div className="mt-4 flex items-end justify-between text-[10px] text-gray-400">
                        {/*<div className="font-mono">ID: {id}</div>*/}
                        <div className="flex gap-1">
                            <span className="w-1 h-5 bg-gray-300 rounded-sm" />
                            <span className="w-1 h-4 bg-gray-300 rounded-sm" />
                            <span className="w-1 h-6 bg-gray-300 rounded-sm" />
                            <span className="w-1 h-3 bg-gray-300 rounded-sm" />
                        </div>
                    </div>
                </div>
            </div>

            {/* --- آکاردئون برای بقیه اطلاعات --- */}
            <div className="border-t border-dashed border-gray-200 bg-gray-50/60">
                {/* تب‌ها */}
                <div className="flex flex-wrap gap-6 px-4 pt-3 text-xs md:text-sm border-b border-gray-200">
                    <button
                        type="button"
                        onClick={() => toggleSection(SECTION_KEYS.BUYER)}
                        className={`flex items-center gap-2 pb-2 px-2 -mb-px text-xs font-medium transition-colors border-b-2 ${
                            openKey === SECTION_KEYS.BUYER
                                ? 'border-emerald-600 text-emerald-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <svg
                            className="w-4 h-4 text-emerald-600"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        اطلاعات خریدار
                    </button>

                    <button
                        type="button"
                        onClick={() => toggleSection(SECTION_KEYS.FINANCIAL)}
                        className={`flex items-center gap-2 pb-2 px-2 -mb-px text-xs font-medium transition-colors border-b-2 ${
                            openKey === SECTION_KEYS.FINANCIAL
                                ? 'border-emerald-600 text-emerald-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <svg
                            className="w-4 h-4 text-emerald-600"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V6m0 10v2" />
                        </svg>
                        جزئیات مالی
                    </button>

                    <button
                        type="button"
                        onClick={() => toggleSection(SECTION_KEYS.TIMELINE)}
                        className={`flex items-center gap-2 pb-2 px-2 -mb-px text-xs font-medium transition-colors border-b-2 ${
                            openKey === SECTION_KEYS.TIMELINE
                                ? 'border-emerald-600 text-emerald-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <svg
                            className="w-4 h-4 text-emerald-600"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path d="M12 6v6l4 2" />
                        </svg>
                        تاریخچه و وضعیت
                    </button>

                    <button
                        type="button"
                        onClick={() => toggleSection(SECTION_KEYS.META)}
                        className={`flex items-center gap-2 pb-2 px-2 -mb-px text-xs font-medium transition-colors border-b-2 ${
                            openKey === SECTION_KEYS.META
                                ? 'border-emerald-600 text-emerald-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <svg
                            className="w-4 h-4 text-emerald-600"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path d="M12 4v16m8-8H4" />
                        </svg>
                        سایر موارد
                    </button>
                </div>

                {/* محتوای آکاردئون */}
                {openKey && (
                    <div className="px-4 pb-4 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-[13px] text-gray-700 text-right">
                        {openKey === SECTION_KEYS.BUYER && (
                            <>
                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <path d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">نام خریدار</div>
                                        <div className="text-sm font-medium text-gray-900">{buyerName}</div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <path d="M3 5h2l3 10h11" />
                      <path d="M16 13a4 4 0 01-8 0" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">موبایل</div>
                                        <div dir="ltr" className="text-sm font-mono text-gray-900">
                                            {buyerPhone}
                                        </div>
                                    </div>
                                </div>

                                {license.buyer?.email && (
                                    <div className="flex items-start gap-2">
                    <span className="mt-0.5">
                      <svg
                          className="w-4 h-4 text-emerald-600"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                      >
                        <path d="M4 4h16v16H4z" />
                        <path d="M4 4l8 8 8-8" />
                      </svg>
                    </span>
                                        <div>
                                            <div className="text-[11px] text-gray-500 font-semibold">ایمیل</div>
                                            <div dir="ltr" className="text-xs font-mono text-gray-900">
                                                {license.buyer.email}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {license.buyer?.address && (
                                    <div className="flex items-start gap-2">
                    <span className="mt-0.5">
                      <svg
                          className="w-4 h-4 text-emerald-600"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                      >
                        <path d="M12 2C8.686 2 6 4.686 6 8c0 4.418 6 12 6 12s6-7.582 6-12c0-3.314-2.686-6-6-6z" />
                        <circle cx="12" cy="8" r="2.5" />
                      </svg>
                    </span>
                                        <div>
                                            <div className="text-[11px] text-gray-500 font-semibold">آدرس</div>
                                            <div className="text-xs md:text-[13px] text-gray-900">
                                                {license.buyer.address}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {(license.buyer?.complexname || license.buyer?.complexnumber) && (
                                    <div className="flex items-start gap-2">
                    <span className="mt-0.5">
                      <svg
                          className="w-4 h-4 text-emerald-600"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                      >
                        <path d="M4 21V5a2 2 0 012-2h4l2 2h6a2 2 0 012 2v14" />
                      </svg>
                    </span>
                                        <div>
                                            <div className="text-[11px] text-gray-500 font-semibold">مجموعه</div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {license.buyer?.complexname || '—'}
                                            </div>
                                            <div className="text-[11px] text-gray-500 mt-0.5">
                                                تلفن مجموعه:{' '}
                                                <span dir="ltr" className="font-mono">
                          {license.buyer?.complexnumber || '—'}
                        </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {openKey === SECTION_KEYS.FINANCIAL && (
                            <>
                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <path d="M3 6h18v4H3z" />
                      <path d="M3 14h18v4H3z" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">قیمت مجاز</div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {fa.format(price / 10)} <span className="text-xs">تومان</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <path d="M12 3v18" />
                      <path d="M8 7h4a3 3 0 010 6h-4a3 3 0 000 6h4" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">
                                            درصد کمیسیون نماینده
                                        </div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {fa.format(
                                                Number(
                                                    license.agent_commission_pct ??
                                                    license.product?.agent_commission_pct ??
                                                    0,
                                                ),
                                            )}
                                            %
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2" />
                      <path d="M12 6v2m0 10v2" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">
                                            کمیسیون شما (هر لایسنس)
                                        </div>
                                        <div className="text-sm font-bold text-emerald-700">
                                            {fa.format(perUnitCommission / 10)}
                                            <span className="text-xs mr-1">تومان</span>
                                        </div>
                                    </div>
                                </div>

                                {license.product?.price_annual && (
                                    <div className="flex items-start gap-2">
                    <span className="mt-0.5">
                      <svg
                          className="w-4 h-4 text-emerald-600"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                      >
                        <path d="M3 5h18v14H3z" />
                        <path d="M3 10h18" />
                      </svg>
                    </span>
                                        <div>
                                            <div className="text-[11px] text-gray-500 font-semibold">
                                               مبلغ اشتراک سالیانه
                                            </div>
                                            <div className="text-xs font-mono text-gray-900">
                                                {fa.format(license.product.price_annual / 10)}{' '}
                                                <span className="text-[10px]"></span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {openKey === SECTION_KEYS.TIMELINE && (
                            <>
                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <span
                        className={`inline-block w-2 h-2 rounded-full ${
                            humanActive(license) === 'فعال'
                                ? 'bg-emerald-500'
                                : 'bg-gray-400'
                        }`}
                    />
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">وضعیت فعلی</div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {humanActive(license)}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">تاریخ صدور</div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {faDate(license.issued_at)}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">تاریخ انقضا</div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {faDate(license.expires_at)}
                                        </div>
                                    </div>
                                </div>

                                {license.created_at && (
                                    <div className="flex items-start gap-2">
                    <span className="mt-0.5">
                      <svg
                          className="w-4 h-4 text-emerald-600"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                      >
                        <path d="M5 12h14" />
                        <path d="M12 5l7 7-7 7" />
                      </svg>
                    </span>
                                        <div>
                                            <div className="text-[11px] text-gray-500 font-semibold">
                                                ایجاد در سیستم
                                            </div>
                                            <div className="text-xs text-gray-900">
                                                {faDate(license.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {license.updated_at && (
                                    <div className="flex items-start gap-2">
                    <span className="mt-0.5">
                      <svg
                          className="w-4 h-4 text-emerald-600"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                      >
                        <path d="M21 12a9 9 0 11-9-9" />
                        <path d="M12 7v5l3 3" />
                      </svg>
                    </span>
                                        <div>
                                            <div className="text-[11px] text-gray-500 font-semibold">
                                                آخرین بروزرسانی
                                            </div>
                                            <div className="text-xs text-gray-900">
                                                {faDate(license.updated_at)}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {openKey === SECTION_KEYS.META && (
                            <>
                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <path d="M4 4h16v16H4z" />
                      <path d="M4 9h16" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">
                                            شناسه نماینده
                                        </div>
                                        <div className="text-sm font-mono text-gray-900">{license.agent_id}</div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <path d="M4 4h16v16H4z" />
                      <path d="M4 9h16" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">
                                            شناسه محصول
                                        </div>
                                        <div className="text-sm font-mono text-gray-900">{license.product_id}</div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <path d="M4 4h16v16H4z" />
                      <path d="M4 9h16" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">
                                            شناسه فاکتور
                                        </div>
                                        <div className="text-sm font-mono text-gray-900">{license.invoice_id}</div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2">
                  <span className="mt-0.5">
                    <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                  </span>
                                    <div>
                                        <div className="text-[11px] text-gray-500 font-semibold">
                                            وضعیت متادیتا
                                        </div>
                                        <div className="text-sm text-gray-900">
                                            {license.metadata ? 'ثبت شده' : '—'}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
